import React, { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase-auth'
import { useAuth } from '../../../contexts/AuthContextLazy'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { TemplateEditor } from '../../../components/portal/TemplateEditor'
import { TemplatePreview } from '../../../components/portal/TemplatePreview'

interface ProjectTemplate {
  id: string
  name: string
  project_type: string
  description: string | null
  icon: string | null
  default_duration_days: number | null
  milestones: any[]
  tasks: any[]
  is_active: boolean
  usage_count: number
  created_by: string | null
  created_at: string
  updated_at: string
}

export const ProjectTemplates: React.FC = () => {
  const { user } = useAuth()
  const [templates, setTemplates] = useState<ProjectTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [showEditor, setShowEditor] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ProjectTemplate | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState<ProjectTemplate | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)

  const projectTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'insurance_policy', label: 'Insurance Policy' },
    { value: 'investment_transfer', label: 'Investment Transfer' },
    { value: 'estate_planning', label: 'Estate Planning' },
    { value: 'tax_planning', label: 'Tax Planning' },
    { value: 'retirement_planning', label: 'Retirement Planning' },
    { value: 'education_planning', label: 'Education Planning' },
    { value: 'business_planning', label: 'Business Planning' },
    { value: 'other', label: 'Other' }
  ]

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('project_templates')
        .select('*')
        .order('usage_count', { ascending: false })

      if (error) throw error
      setTemplates(data || [])
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching templates:', error)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async (templateId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('project_templates')
        .update({ 
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId)

      if (error) throw error
      fetchTemplates()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error toggling template status:', error)
      }
    }
  }

  const handleCloneTemplate = async (template: ProjectTemplate) => {
    try {
      const clonedTemplate = {
        ...template,
        id: `${template.id}-clone-${Date.now()}`,
        name: `${template.name} (Copy)`,
        usage_count: 0,
        created_by: user?.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      delete (clonedTemplate as any).id // Let DB generate new ID

      const { error } = await supabase
        .from('project_templates')
        .insert(clonedTemplate)

      if (error) throw error
      fetchTemplates()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error cloning template:', error)
      }
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      const { error } = await supabase
        .from('project_templates')
        .delete()
        .eq('id', templateId)

      if (error) throw error
      setShowDeleteConfirm(null)
      fetchTemplates()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error deleting template:', error)
      }
    }
  }

  const getProjectTypeIcon = (type: string) => {
    switch (type) {
      case 'insurance_policy': return '🛡️'
      case 'investment_transfer': return '💼'
      case 'estate_planning': return '📋'
      case 'tax_planning': return '📊'
      case 'retirement_planning': return '🎯'
      case 'education_planning': return '🎓'
      case 'business_planning': return '🏢'
      default: return '📁'
    }
  }

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || template.project_type === filterType
    return matchesSearch && matchesType
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => {
            setEditingTemplate(null)
            setShowEditor(true)
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
          Create Template
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white portal-content-shadow rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              {projectTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="text-right text-sm text-gray-600">
            {filteredTemplates.length} templates found
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div 
            key={template.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <span className="text-3xl mr-3">
                    {template.icon || getProjectTypeIcon(template.project_type)}
                  </span>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">
                      {template.project_type.replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleActive(template.id, template.is_active)}
                  className={`p-1 rounded-full ${
                    template.is_active 
                      ? 'text-green-600 hover:bg-green-100' 
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={template.is_active ? 'Active' : 'Inactive'}
                >
                  {template.is_active ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
                </button>
              </div>

              {template.description && (
                <p className="text-sm text-gray-600 mb-4">{template.description}</p>
              )}

              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <div className="flex items-center space-x-4">
                  <span>{template.milestones?.length || 0} milestones</span>
                  <span>{template.tasks?.length || 0} tasks</span>
                </div>
                {template.default_duration_days && (
                  <span>{template.default_duration_days} days</span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1 text-sm">
                  <ChartBarIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Used {template.usage_count} times</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => {
                      setPreviewTemplate(template)
                      setShowPreview(true)
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Preview"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingTemplate(template)
                      setShowEditor(true)
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Edit"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleCloneTemplate(template)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Clone"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(template.id)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Delete Confirmation */}
            {showDeleteConfirm === template.id && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <p className="text-sm text-gray-600 mb-3">
                  Are you sure you want to delete this template?
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterType !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first template'}
          </p>
        </div>
      )}

      {/* Template Editor Modal */}
      {showEditor && (
        <TemplateEditor
          template={editingTemplate}
          onSave={() => {
            setShowEditor(false)
            setEditingTemplate(null)
            fetchTemplates()
          }}
          onCancel={() => {
            setShowEditor(false)
            setEditingTemplate(null)
          }}
        />
      )}

      {/* Template Preview Modal */}
      {showPreview && previewTemplate && (
        <TemplatePreview
          template={previewTemplate}
          onClose={() => {
            setShowPreview(false)
            setPreviewTemplate(null)
          }}
        />
      )}
    </div>
  )
}